import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    Eye,
    InfoCircle,
    AlertCircle,
    ChevronUp,
    ChevronDown,
    CheckCircle,
    Clock,
    RefreshCcw01,
    XCircle,
    CheckCircleBroken,
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { BadgeWithIcon } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Toggle } from '@/components/base/toggle/toggle';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { DialogTrigger as AriaDialogTrigger, Popover as AriaPopover, Button as AriaButton } from 'react-aria-components';
import { useWorkflows, useDeleteWorkflow, useUpdateWorkflow } from '@/hooks/api/use-workflows';
import type { WorkflowStatus } from '@/types/workflow';
import type { WorkflowResponse } from '@/types/api/workflows';
import { useTranslation } from 'react-i18next';

// Helper to get badge color and icon for workflow status
const getStatusBadgeConfig = (status: WorkflowStatus): { color: "gray" | "brand" | "success" | "warning" | "error"; icon: typeof CheckCircle } => {
    switch (status) {
        case 'draft':
            return { color: 'gray', icon: Clock };
        case 'analyzing':
            return { color: 'brand', icon: RefreshCcw01 };
        case 'ready':
            return { color: 'success', icon: CheckCircle };
        case 'needs_clarification':
            return { color: 'warning', icon: AlertCircle };
        case 'invalid':
            return { color: 'error', icon: XCircle };
        case 'disabled':
            return { color: 'gray', icon: CheckCircleBroken };
        default:
            return { color: 'gray', icon: Clock };
    }
};

type SortColumn = 'created_at' | 'nodes_count' | 'analyzed_at';
type SortDirection = 'asc' | 'desc';

export const WorkflowsListPage = () => {
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
    const [workflowToDeleteIsDefault, setWorkflowToDeleteIsDefault] = useState(false);

    // Sorting state
    const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const statusOptions = [
        { id: '', label: t('workflows.statusPlaceholder') },
        { id: 'draft', label: t('workflows.status.draft') },
        { id: 'analyzing', label: t('workflows.status.analyzing') },
        { id: 'ready', label: t('workflows.status.ready') },
        { id: 'needs_clarification', label: t('workflows.status.needs_clarification') },
        { id: 'invalid', label: t('workflows.status.invalid') },
        { id: 'disabled', label: t('workflows.status.disabled') },
    ];

    // Use API hooks
    const { data: workflows = [], isLoading } = useWorkflows({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
    });

    const deleteWorkflowMutation = useDeleteWorkflow();
    const updateWorkflowMutation = useUpdateWorkflow();

    // Sort workflows
    const sortedWorkflows = useMemo(() => {
        return [...workflows].sort((a, b) => {
            let comparison = 0;
            if (sortColumn === 'created_at') {
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            } else if (sortColumn === 'nodes_count') {
                comparison = (a.nodes?.length || 0) - (b.nodes?.length || 0);
            } else if (sortColumn === 'analyzed_at') {
                const aTime = a.analyzed_at ? new Date(a.analyzed_at).getTime() : 0;
                const bTime = b.analyzed_at ? new Date(b.analyzed_at).getTime() : 0;
                comparison = aTime - bTime;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [workflows, sortColumn, sortDirection]);

    // Handle sorting
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    // Handle is_active toggle
    const handleActiveToggle = async (workflow: WorkflowResponse, newValue: boolean) => {
        try {
            await updateWorkflowMutation.mutateAsync({
                workflowId: workflow.workflow_id,
                data: { is_active: newValue },
            });
        } catch (error) {
            console.error('Error updating workflow active status:', error);
        }
    };

    const handleDeleteClick = (workflow: WorkflowResponse) => {
        setWorkflowToDelete(workflow.workflow_id);
        setWorkflowToDeleteIsDefault(workflow.is_default);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (workflowToDelete) {
            try {
                await deleteWorkflowMutation.mutateAsync(workflowToDelete);
                setIsDeleteModalOpen(false);
                setWorkflowToDelete(null);
            } catch (error) {
                // Error already handled by mutation (toast)
                console.error('Error deleting workflow:', error);
            }
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setWorkflowToDelete(null);
        setWorkflowToDeleteIsDefault(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('workflows.title')}</h2>
                    <p className="text-tertiary mt-1">{t('workflows.description')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/workflows/new', { state: navigationStateFrom(location) })}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('workflows.newWorkflow')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('workflows.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('workflows.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as WorkflowStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Workflows Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('workflows.title')} (${workflows.length})`}
                    description={t('workflows.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('workflows.headers.name')} isRowHeader />
                        <Table.Head label={t('workflows.headers.type')} />
                        <Table.Head label={t('workflows.headers.status')} />
                        <Table.Head label={t('workflows.headers.active')} />
                        {/* Sortable: Nodes Count */}
                        <Table.Head>
                            <button
                                type="button"
                                onClick={() => handleSort('nodes_count')}
                                className="flex items-center gap-1 text-xs font-medium uppercase text-tertiary hover:text-secondary cursor-pointer"
                            >
                                {t('workflows.headers.nodesCount')}
                                {sortColumn === 'nodes_count' ? (
                                    sortDirection === 'asc' ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />
                                ) : (
                                    <ChevronDown className="size-3 opacity-30" />
                                )}
                            </button>
                        </Table.Head>
                        {/* Sortable: Last AI Evaluation */}
                        <Table.Head>
                            <button
                                type="button"
                                onClick={() => handleSort('analyzed_at')}
                                className="flex items-center gap-1 text-xs font-medium uppercase text-tertiary hover:text-secondary cursor-pointer"
                            >
                                {t('workflows.headers.lastAiEvaluation')}
                                {sortColumn === 'analyzed_at' ? (
                                    sortDirection === 'asc' ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />
                                ) : (
                                    <ChevronDown className="size-3 opacity-30" />
                                )}
                            </button>
                        </Table.Head>
                        {/* Sortable: Created At */}
                        <Table.Head>
                            <button
                                type="button"
                                onClick={() => handleSort('created_at')}
                                className="flex items-center gap-1 text-xs font-medium uppercase text-tertiary hover:text-secondary cursor-pointer"
                            >
                                {t('workflows.headers.createdAt')}
                                {sortColumn === 'created_at' ? (
                                    sortDirection === 'asc' ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />
                                ) : (
                                    <ChevronDown className="size-3 opacity-30" />
                                )}
                            </button>
                        </Table.Head>
                        <Table.Head label={t('workflows.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {sortedWorkflows.map((workflow: WorkflowResponse) => (
                            <Table.Row key={workflow.workflow_id}>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-primary">{workflow.name}</div>
                                        {workflow.description && (
                                            <AriaDialogTrigger>
                                                <AriaButton className="flex cursor-pointer items-center justify-center rounded-md p-0.5 text-tertiary outline-focus-ring transition duration-100 ease-linear hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-2">
                                                    <InfoCircle className="size-4" />
                                                </AriaButton>
                                                <AriaPopover
                                                    placement="bottom start"
                                                    offset={8}
                                                    className="max-w-xs rounded-lg bg-primary p-3 shadow-lg ring-1 ring-secondary outline-hidden"
                                                >
                                                    <div className="text-sm text-primary">{workflow.description}</div>
                                                </AriaPopover>
                                            </AriaDialogTrigger>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <BadgeWithIcon
                                        type="color"
                                        size="sm"
                                        color={workflow.is_default ? 'brand' : 'gray'}
                                        iconLeading={workflow.is_default ? CheckCircle : undefined}
                                    >
                                        {workflow.is_default ? t('workflows.type.default') : t('workflows.type.custom')}
                                    </BadgeWithIcon>
                                </Table.Cell>
                                <Table.Cell>
                                    <BadgeWithIcon
                                        type="color"
                                        size="sm"
                                        color={getStatusBadgeConfig(workflow.status).color}
                                        iconLeading={getStatusBadgeConfig(workflow.status).icon}
                                    >
                                        {t(`workflows.status.${workflow.status}`)}
                                    </BadgeWithIcon>
                                </Table.Cell>
                                <Table.Cell>
                                    <Toggle
                                        isSelected={workflow.is_active}
                                        onChange={(value) => handleActiveToggle(workflow, value)}
                                        size="sm"
                                        isDisabled={workflow.status !== 'ready' && workflow.status !== 'disabled'}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{workflow.nodes?.length || 0}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {workflow.analyzed_at
                                            ? new Date(workflow.analyzed_at).toLocaleDateString() + ' ' + new Date(workflow.analyzed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : '-'
                                        }
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {new Date(workflow.created_at).toLocaleDateString()}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/workflows/${workflow.workflow_id}`}
                                            state={navigationStateFrom(location)}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            {t('workflows.actions.view')}
                                            <Eye className="size-4" />
                                        </Link>
                                        <Button
                                            onClick={() => navigate(`/admin/workflows/${workflow.workflow_id}/edit`, { state: navigationStateFrom(location) })}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('workflows.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(workflow)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('workflows.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {workflows.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.noData.title')}</h3>
                        <p className="text-tertiary">{t('workflows.noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                {workflowToDeleteIsDefault ? (
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <AlertCircle className="size-6 text-utility-warning-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-xl font-semibold text-primary mb-2">
                                                {t('workflows.modal.deleteDefaultWorkflow.title')}
                                            </h2>
                                            <p className="text-tertiary mb-6">
                                                {t('workflows.modal.deleteDefaultWorkflow.message')}
                                            </p>
                                            <div className="flex items-center justify-end gap-3">
                                                <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                                    {t('common:modal.cancel')}
                                                </Button>
                                                <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                                    {t('common:modal.delete')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-xl font-semibold text-primary mb-4">
                                            {t('workflows.modal.confirmDelete')}
                                        </h2>
                                        <p className="text-tertiary mb-6">
                                            {t('workflows.modal.confirmDeleteMessage')}
                                        </p>
                                        <div className="flex items-center justify-end gap-4">
                                            <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                                {t('common:modal.cancel')}
                                            </Button>
                                            <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                                {t('common:modal.delete')}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
